import { ChangeEvent } from 'react';

import { Control, Path, PathValue } from 'react-hook-form';
import { z } from 'zod';

import { cn } from '@/lib/utils';

import { Combobox } from '../ui/combobox';
import { FormFieldItem } from '../ui/form';
import { Input } from '../ui/input';
import { CreatableMultiSelect, MULTI_SELECT_STYLES } from '../ui/multi-select';

const locationFormSchema = z.object({
  name: z.string().min(2, {
    message: 'Required',
  }),
  status: z.boolean(),
  emails: z.array(z.object({ label: z.string(), value: z.string() })),
  address: z.string().optional(),
  placeId: z.string().optional(),
  radius: z.number().optional(),
  zoom: z.number().optional(),
  lat: z.number().optional(),
  lng: z.number().optional(),
});

type LocationFormSchemaType = z.infer<typeof locationFormSchema>;

function LocationSelectField({
  label,
  name,
  control,
  options,
  required,
  renderItem,
  onChange: onChangeOverride,
  labelSize,
  disabled,
}: {
  label: string;
  name: Path<LocationFormSchemaType>;
  control: Control<LocationFormSchemaType>;
  options: { label: string; value: string }[];
  required?: boolean;
  renderItem?: (option: { value: string; label: string }, isSelected: boolean) => React.ReactNode;
  onChange?: (value: string) => void;
  labelSize?: 'base' | 'small';
  disabled?: boolean;
}) {
  return (
    <FormFieldItem<LocationFormSchemaType, Path<LocationFormSchemaType>> control={control} name={name} label={label} required={required} labelSize={labelSize}>
      {({ value, onChange, error }) => (
        <Combobox
          aria-label={label}
          value={value as string}
          onChange={(val: string) => {
            if (onChangeOverride) {
              onChangeOverride(val);
            } else {
              onChange(val as PathValue<LocationFormSchemaType, Path<LocationFormSchemaType>>);
            }
          }}
          options={options}
          className={cn('w-full', error && 'border-default-red')}
          renderItem={renderItem}
          disabled={disabled}
        />
      )}
    </FormFieldItem>
  );
}

function LocationInputField({
  label,
  name,
  control,
  placeholder,
  required,
  disabled,
  className,
  labelSize,
}: {
  label: string;
  name: Path<LocationFormSchemaType>;
  control: Control<LocationFormSchemaType>;
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
  className?: string;
  labelSize?: 'base' | 'small';
}) {
  return (
    <FormFieldItem<LocationFormSchemaType, Path<LocationFormSchemaType>>
      control={control}
      name={name}
      label={label}
      required={required}
      labelSize={labelSize}
      className={className}>
      {({ value, onChange, error }) => (
        <Input
          placeholder={placeholder ?? `Enter ${label}...`}
          value={value as string}
          onChange={onChange as unknown as (e: ChangeEvent<HTMLInputElement>) => void}
          error={!!error}
          disabled={disabled}
        />
      )}
    </FormFieldItem>
  );
}

function LocationCreatableMultiSelectField({
  label,
  name,
  control,
  placeholder,
  required,
  defaultOptions,
  className,
  labelSize,
  controlRootClassName,
  controlTriggerClassName,
  disabled,
}: {
  label: string;
  name: Path<LocationFormSchemaType>;
  control: Control<LocationFormSchemaType>;
  placeholder?: string;
  required?: boolean;
  defaultOptions: { label: string; value: string }[];
  labelSize?: 'base' | 'small';
  className?: string;
  controlTriggerClassName?: string;
  controlRootClassName?: string;
  disabled?: boolean;
}) {
  return (
    <FormFieldItem<LocationFormSchemaType, Path<LocationFormSchemaType>>
      control={control}
      name={name}
      label={label}
      required={required}
      labelSize={labelSize}
      className={className}>
      {({ value, onChange }) => (
        <CreatableMultiSelect<{ label: string; value: string }>
          options={defaultOptions}
          placeholder={placeholder ?? `Enter ${label}...`}
          value={value as unknown as { label: string; value: string }[]}
          onChange={(value) => onChange(value as PathValue<LocationFormSchemaType, Path<LocationFormSchemaType>>)}
          isDisabled={disabled}
          classNames={(() => {
            const styles = MULTI_SELECT_STYLES<{ label: string; value: string }>();

            return {
              ...styles,
              container: (props) => cn(styles.container?.(props), controlRootClassName),
              control: (props) => cn(styles.control?.(props), controlTriggerClassName),
            };
          })()}
        />
      )}
    </FormFieldItem>
  );
}

export { locationFormSchema, LocationSelectField, LocationInputField, LocationCreatableMultiSelectField };
